import React, { useEffect, useState } from "react";
import {
  DropdownMenuReduced,
  DropdownMenuReducedContent,
  DropdownMenuReducedItem,
  DropdownMenuReducedTrigger
} from "./DropdownMenuReduced";
import { IoMdCheckmark } from "react-icons/io";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { isEmpty, keys } from "lodash";

export interface DropdownOptions {
  [key: string]: string | JSX.Element;
}
export type dropdownPropsType = {
  className?: string;
  label?: string;
  options: DropdownOptions;
  fixedOptions?: DropdownOptions;
  defaultValue: string;
  valueSelected?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  popOverFooter?: JSX.Element;
  hideArrow?: boolean;
  dataTest?: string;
  customIcon?: JSX.Element;
  selectedOptionClassName?: string;
  menuItemClassName?: string;
  readOnly?: boolean;
  containerClassName?: string;
  onOpenChange?: (open: boolean) => void;
  labelClassName?: string;
  sideOffset?: number;
  align?: "center" | "end" | "start";
  hideBorder?: boolean;
};

const Dropdown: React.FC<dropdownPropsType> = ({
  className,
  label = "",
  options,
  fixedOptions = {},
  defaultValue,
  valueSelected,
  disabled = false,
  placeholder,
  value: controlledValue,
  popOverFooter,
  hideArrow = false,
  dataTest = "dropdown",
  customIcon,
  selectedOptionClassName,
  menuItemClassName,
  readOnly = false,
  containerClassName,
  onOpenChange,
  labelClassName,
  sideOffset,
  align,
  hideBorder
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  useEffect(() => setInternalValue(defaultValue), [defaultValue]);

  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleSelect = (currentValue: string) => {
    if (valueSelected) {
      valueSelected(currentValue);
    }
    if (currentValue !== value) {
      setInternalValue(currentValue);
    }
  };
  const renderDropdownItem = (
    currentValue: string,
    itemOptions: DropdownOptions
  ) => (
    <DropdownMenuReducedItem
      key={currentValue}
      data-test={currentValue}
      onClick={() => !readOnly && handleSelect(currentValue)}
      unselectable={currentValue === value ? "on" : "off"}
      className={menuItemClassName}
    >
      {itemOptions[currentValue]}
      {currentValue === value && <IoMdCheckmark className="size-5" />}
    </DropdownMenuReducedItem>
  );

  return (
    <div
      className={cn("flex items-center gap-x-1.5 gap-y-1", containerClassName)}
    >
      {label && (
        <Text
          as="label"
          variant="bodySecondary"
          className={cn("text-nowrap", labelClassName)}
        >
          {label}
        </Text>
      )}
      <DropdownMenuReduced onOpenChange={onOpenChange}>
        <DropdownMenuReducedTrigger
          data-test={dataTest}
          className={cn(
            className,
            // Read-only keeps the value at full strength — it is valid, just not
            // editable here. Only `disabled` dims.
            readOnly && "cursor-default",
            disabled && "opacity-50"
          )}
          disabled={disabled || readOnly}
          hideArrow={hideArrow || readOnly}
          customIcon={!readOnly ? customIcon : undefined}
          hideBorder={hideBorder}
        >
          {placeholder && isEmpty(value) && (
            <Text data-test="placeholder" as="span" variant="bodySecondary">
              {placeholder}
            </Text>
          )}
          <Text
            data-test="selected-option"
            as="span"
            variant={placeholder ? "body" : "bodySecondary"}
            className={selectedOptionClassName}
          >
            {fixedOptions[value] || options[value]}
          </Text>
        </DropdownMenuReducedTrigger>
        <DropdownMenuReducedContent
          className={cn("max-h-[32vh]", className)}
          sideOffset={sideOffset}
          align={align}
        >
          <div className="flex flex-col h-full overflow-y-auto">
            {!isEmpty(fixedOptions) && (
              <>
                {keys(fixedOptions).map((currentValue) =>
                  renderDropdownItem(currentValue, fixedOptions)
                )}
                <div className="border-b border-border-border2 my-1" />
              </>
            )}
            {keys(options).map((currentValue) =>
              renderDropdownItem(currentValue, options)
            )}
          </div>
          {popOverFooter && popOverFooter}
        </DropdownMenuReducedContent>
      </DropdownMenuReduced>
    </div>
  );
};

export default Dropdown;
